import { useAsyncData, useState } from '#imports'
import { computed, onUnmounted } from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import type { RecommendationAlgoliaCarouselProps } from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { useAlgoliaRecommendations } from '@backmarket/nuxt-layer-recommendation/useAlgoliaRecommendations'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import {
  getBrazeInstance,
  whenBrazeIsReady,
} from '@backmarket/nuxt-module-tracking/getBrazeInstance'

import { useSearchProducts } from '../../../products/useSearchProducts'
import type { RefinedProductsBlockProps } from '../../RefinedProducts/RefinedProducts.vue'
import {
  type BrazeContentCard,
  type ContentCard,
  type SupportedFallbackTypes,
  transformContentCards,
  transformFallbackBlocks,
} from '../PersonalizedFeed.utils'

async function getBrazeCards({
  triggerRefresh,
}: {
  triggerRefresh: boolean
}): Promise<BrazeContentCard[]> {
  const instance = getBrazeInstance()

  if (!instance) {
    return []
  }
  const cachedCards = instance.getCachedContentCards()?.cards || []

  if (cachedCards.length > 0) {
    // make sure to refresh the cached cards to have fresh data on the next page load
    // TODO: discuss on a cache TTL for which we can keep cached cards
    instance.requestContentCardsRefresh()

    return cachedCards
  }

  const contentCardsPromise = new Promise<BrazeContentCard[]>((resolve) => {
    instance.subscribeToContentCardsUpdates((updates) => {
      resolve(updates.cards)
    })
  })

  // this ensures that only one PersonalizedFeed block triggers a refresh at the same time
  if (triggerRefresh) {
    instance.requestContentCardsRefresh()
    instance.requestImmediateDataFlush()
  }

  return contentCardsPromise
}

export function usePersonalizedFeed({
  feed,
  productQuery,
  fallback,
  title,
  blockPosition,
}: {
  feed: string
  productQuery: PersonalizedFeedApiProps['productQuery']
  fallback: PersonalizedFeedApiProps['fallback']
  blockPosition: number
  title: string
}) {
  const brazeRefreshTriggered = useState('braze-refresh-triggered', () => false)

  onUnmounted(() => {
    brazeRefreshTriggered.value = false
  })

  const { data, pending, error } = useAsyncData(
    getAsyncDataKey('personalized-feed', { feed }),
    async (): Promise<{
      cards: ContentCard[]
      source: 'fallback' | 'content-card'
    }> => {
      const isEnabled = await whenBrazeIsReady()

      if (!isEnabled) {
        const fallbackContentCards = transformFallbackBlocks({
          fallback,
          blockPosition,
          title,
        })

        const successfulFallbackCards =
          await fetchContentCardsDataAndKeepOnlySuccessful(fallbackContentCards)

        return {
          cards: successfulFallbackCards,
          source: 'fallback',
        }
      }

      const doTriggerRefresh = !brazeRefreshTriggered.value

      if (doTriggerRefresh) {
        brazeRefreshTriggered.value = true
      }

      const brazeCards = await getBrazeCards({
        triggerRefresh: doTriggerRefresh,
      })

      const cards = transformContentCards({
        cards: brazeCards,
        feed,
        productQuery,
        blockPosition,
      })

      const successfulCards =
        await fetchContentCardsDataAndKeepOnlySuccessful(cards)

      if (successfulCards.length === 0) {
        const fallbackContentCards = transformFallbackBlocks({
          fallback,
          blockPosition,
          title,
        })

        const successfulFallbackCards =
          await fetchContentCardsDataAndKeepOnlySuccessful(fallbackContentCards)

        return {
          cards: successfulFallbackCards,
          source: 'fallback',
        }
      }

      return {
        cards: successfulCards,
        source: 'content-card',
      }
    },
    {
      server: false,
      immediate: true,
    },
  )

  const cards = computed(() => data.value?.cards || [])
  const source = computed(() => data.value?.source)

  return {
    cards,
    source,
    pending,
    error,
  }
}

async function fetchContentCardsDataAndKeepOnlySuccessful(
  cards: ContentCard[],
): Promise<ContentCard[]> {
  const useAsyncDataList: Array<{
    successfulRequestPromise: Promise<boolean>
  }> = cards.map((card) => {
    const props =
      'props' in card.props
        ? (card.props as SupportedFallbackTypes).props
        : card.props

    if ('productQuery' in props) {
      const { productQuery: pq, sortFn } = props as RefinedProductsBlockProps

      const asyncDataPromise = useSearchProducts(
        {
          productQuery: pq,
        },
        {
          immediate: true,
          server: false,
          sort: sortFn,
        },
      )

      return {
        // promise that resolves to true once the async data is fetched and is successful
        successfulRequestPromise: asyncDataPromise.then(
          (asyncData) =>
            !!(asyncData.data.value && asyncData.data.value.length > 0),
        ),
        asyncDataPromise,
      }
    }
    if ('recommendationQuery' in props) {
      const { recommendationQuery } =
        props as RecommendationAlgoliaCarouselProps

      const asyncDataPromise = useAlgoliaRecommendations(
        recommendationQuery.objectIds,
        recommendationQuery.model,
        recommendationQuery.options,
        {
          server: false,
          cache: true,
        },
      )

      return {
        asyncDataPromise,
        // promise that resolves to true once the async data is fetched and is successful
        successfulRequestPromise: asyncDataPromise.then(
          (asyncData) =>
            !!(asyncData.data.value && asyncData.data.value?.length > 0),
        ),
      }
    }

    // for other card types, there is no async data to fetch, so we automatically resolve to true
    return {
      successfulRequestPromise: Promise.resolve(true),
    }
  })

  const asyncDataResults = await Promise.all(
    useAsyncDataList.map(async ({ successfulRequestPromise }, index) => {
      const successful = await successfulRequestPromise

      return {
        successful,
        index,
      }
    }),
  )

  const successfulCardIndexes = asyncDataResults
    .filter((result) => result.successful)
    .map((result) => result.index)

  const successfulCards = cards.filter((_card, index) =>
    successfulCardIndexes.includes(index),
  )

  return successfulCards
}
